import React, { useEffect, useState, useRef } from "react";
import apiInstance from "../../utils/axios";
import UserData from "../plugin/UserData";
import { useParams, Link } from "react-router-dom";
import moment from 'moment';
import './style/customer-dashboard.css';
import PriceFormat_Basic from '@/components/commerce-ui/price-format-basic';
import { 
  Store, Phone, Mail, MapPin, FileText, ArrowLeft, Printer, 
  User, Package, Calendar, CreditCard, Truck, Tag, CheckCircle 
} from 'lucide-react';

function Invoice() {
  const [order, setOrder] = useState({});
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeInfo, setStoreInfo] = useState({
    store_name: 'ASMAR SHOP',
    phones: ['+595 981 123456'],
    emails: ['contacto@asmarshop.com'],
    address: 'Asunción, Paraguay'
  });

  const userData = UserData();
  const printRef = useRef();

  const param = useParams();

  // Fetch store info
  useEffect(() => {
    const fetchStoreInfo = async () => {
      try {
        const response = await apiInstance.get('store-info/');
        setStoreInfo(response.data);
      } catch (error) {
        console.log('Using default store info');
      }
    };
    fetchStoreInfo();
  }, []);

  useEffect(() => {
    if (userData?.user_id && param.order_oid) {
      setLoading(true);
      apiInstance.get(`customer/order/${userData.user_id}/${param.order_oid}/`)
        .then((res) => {
          setOrder(res.data);
          setOrderItems(res.data.orderitem || []);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching order:', error);
          setError("Error al cargar los detalles de la factura");
          setLoading(false);
        });
    }
  }, [userData?.user_id, param.order_oid]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (printContent) {
      const originalContent = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando factura...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="bg-red-100 text-red-700 px-6 py-4 rounded-lg mb-4">
            {error}
          </div>
          <Link 
            to="/customer/orders/" 
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Pedidos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Factura #{order.oid}</h1>
              <p className="text-sm text-gray-500">
                {order.date ? moment(order.date).format('DD MMM YYYY, HH:mm') : 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link 
              to="/customer/orders/" 
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Volver</span>
            </Link>
            <button 
              onClick={handlePrint} 
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div ref={printRef} className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Invoice Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between gap-6">
              {/* Store Info */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Store className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{storeInfo.store_name || 'ASMAR SHOP'}</h2>
                    <span className="text-blue-200 text-sm">Tu tienda de confianza</span>
                  </div>
                </div>
                <div className="space-y-2 text-blue-100 text-sm">
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {storeInfo.phones?.[0] || '+595 981 123456'}
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {storeInfo.emails?.[0] || 'contacto@asmarshop.com'}
                  </p>
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {storeInfo.address || 'Asunción, Paraguay'}
                  </p>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="text-right sm:text-right">
                <div className="inline-block bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-blue-200 text-sm mb-1">Número de Factura</p>
                  <p className="text-3xl font-bold mb-3">#{order.oid}</p>
                  <div className="flex items-center gap-2 text-sm text-blue-100">
                    <Calendar className="w-4 h-4" />
                    {order.date ? moment(order.date).format('DD/MM/YYYY') : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer & Order Status */}
          <div className="p-6 sm:p-8 border-b border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-800">Cliente</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900">{order.full_name || 'N/A'}</p>
                  <p className="text-gray-600">{order.email || 'N/A'}</p>
                  <p className="text-gray-600">{order.phone || 'N/A'}</p>
                </div>
              </div>

              {/* Shipping Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-800">Dirección de Envío</h3>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>{order.address || 'N/A'}</p>
                  <p>{order.city}, {order.state}</p>
                  <p>{order.country}</p>
                </div>
              </div>
            </div>

            {/* Order Status Badge */}
            {order.order_status && (
              <div className="mt-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Estado:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                  {order.order_status}
                </span>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Productos</h3>
            </div>

            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm">
                    <th className="text-left py-3 px-4 rounded-l-lg">Producto</th>
                    <th className="text-center py-3 px-4">Precio</th>
                    <th className="text-center py-3 px-4">Cantidad</th>
                    <th className="text-center py-3 px-4">Subtotal</th>
                    <th className="text-center py-3 px-4 rounded-r-lg">Descuento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orderItems.length > 0 ? (
                    orderItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={item.product?.image}
                              alt={item.product?.title_translated || item.product?.title}
                              className="w-14 h-14 object-cover rounded-lg border border-gray-200"
                            />
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.product?.title_translated || item.product?.title}
                              </p>
                              <div className="flex gap-2 mt-1">
                                {item.color && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                    {item.color}
                                  </span>
                                )}
                                {item.size && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                    {item.size}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center text-gray-700">
                          <PriceFormat_Basic value={item.price || 0} />
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-lg font-medium">
                            {item.qty}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center font-medium text-gray-900">
                          <PriceFormat_Basic value={item.sub_total || 0} />
                        </td>
                        <td className="py-4 px-4 text-center">
                          {item.saved > 0 ? (
                            <span className="text-green-600 font-medium">
                              -<PriceFormat_Basic value={item.saved} />
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-12 text-center">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No se encontraron artículos</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden space-y-4">
              {orderItems.length > 0 ? (
                orderItems.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex gap-3">
                      <img
                        src={item.product?.image}
                        alt={item.product?.title_translated || item.product?.title}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{item.product?.title_translated || item.product?.title}</p>
                        <div className="flex gap-2 mt-1">
                          {item.color && <span className="text-xs text-gray-500">{item.color}</span>}
                          {item.size && <span className="text-xs text-gray-500">{item.size}</span>}
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-sm text-gray-600">
                            <PriceFormat_Basic value={item.price || 0} /> × {item.qty}
                          </span>
                          <span className="font-semibold text-gray-900">
                            <PriceFormat_Basic value={item.sub_total || 0} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No se encontraron artículos</p>
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="p-6 sm:p-8 bg-gray-50">
            <div className="max-w-sm ml-auto">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-800">Resumen</h3>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium"><PriceFormat_Basic value={order.sub_total || 0} /></span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Envío</span>
                  <span className="font-medium"><PriceFormat_Basic value={order.shipping_ammount || 0} /></span>
                </div>

                {(order.service_fee > 0) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Servicio</span>
                    <span className="font-medium"><PriceFormat_Basic value={order.service_fee || 0} /></span>
                  </div>
                )}
                {order.saved > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      Descuento
                    </span>
                    <span className="font-medium">-<PriceFormat_Basic value={order.saved} /></span>
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-blue-600">
                      <PriceFormat_Basic value={order.total || 0} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 text-center border-t border-gray-100">
            <p className="text-gray-500 text-sm">
              ¡Gracias por su compra en {storeInfo.store_name || 'ASMAR SHOP'}!
            </p>
            <p className="text-gray-400 text-xs mt-1">
              Factura generada automáticamente
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Invoice;
